/**
 * Buildings deleted in this tab, remembered until the tab reloads.
 *
 * Deleting a building drops its queries from the cache, but the building page
 * can still be on screen at that moment: leaving for the dashboard is a route
 * transition, not an immediate unmount. An active query whose cache entry is
 * removed refetches at once, the API answers 404 for the row that no longer
 * exists, and the page paints that raw error ("No query results for model
 * [App\Models\Project] <id>") on top of the "Building deleted" toast.
 *
 * Remembering the id keeps the detail query switched off for the rest of the
 * page's life, so nothing asks the API about a building we ourselves deleted.
 * The id is forgotten when the building comes back: Undo in this tab, or a
 * buildings list requested AFTER the deletion that contains it again (Undo in
 * another tab, restore from Trash). Without the second path a building that
 * was deleted and restored elsewhere stayed unopenable in this tab until a
 * reload.
 *
 * Removing this makes the 404 flash come back.
 */

// projectId → when this tab learned it was deleted (Date.now()).
const deleted = new Map<string, number>();

export function markBuildingDeleted(projectId: string): void {
  deleted.set(projectId, Date.now());
}

export function forgetDeletedBuilding(projectId: string): void {
  deleted.delete(projectId);
}

export function isBuildingDeleted(projectId: string): boolean {
  return deleted.has(projectId);
}

/**
 * The API listed these buildings in a response to a request sent at
 * `requestedAt`. A list requested before the deletion may still carry the
 * building, so only a later one counts as proof it is back.
 */
export function noteBuildingsListed(
  projectIds: readonly string[],
  requestedAt: number
): void {
  for (const id of projectIds) {
    const deletedAt = deleted.get(id);
    if (deletedAt !== undefined && requestedAt > deletedAt) {
      deleted.delete(id);
    }
  }
}
